import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import ServerStatus from "../components/ServerStatus";
import AuthBrandPanel from "../components/AuthBrandPanel";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { Eye, EyeOff, Loader2, ArrowRight, AlertTriangle } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const routeAfterLogin = (user) => {
    if (user.requires_password_change) navigate("/change-password");
    else if (user.role === "company" || user.role === "company_employee") navigate("/company/roles");
    else navigate("/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      routeAfterLogin(user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setSubmitting(false); }
  };

  const handleGoogleCredential = async (credential, role) => {
    try {
      const user = await loginWithGoogle(credential, role);
      toast.success("Welcome back");
      routeAfterLogin(user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Google sign-in failed");
    }
  };

  return (
    <main className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

      {/* ══════════════ LEFT — FORM ══════════════ */}
      <div className="flex items-center justify-center p-6 lg:p-14 bg-white">
        <div className="w-full max-w-[400px]">
          {/* Mobile wordmark */}
          <Link to="/" className="lg:hidden mb-8 inline-block">
            <Logo className="h-9 w-auto" />
          </Link>

          <ServerStatus />

          {params.get("err") && (
            <div className="mb-6 flex items-center gap-2 rounded-xl border border-[#E11D48]/30 bg-[#E11D48]/5 px-4 py-3 text-sm text-[#E11D48]">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              Google sign-in failed. Please try again.
            </div>
          )}

          <div className="font-mono text-[10px] tracking-[0.25em] text-[#0025D8] mb-3">LOG IN</div>
          <h1 className="font-display font-black text-[2.6rem] leading-[1.1] tracking-tight text-[#0F172A] mb-2">
            Welcome back.
          </h1>
          <p className="text-sm text-muted-foreground mb-8">Sign in to pick up your prep where you left off.</p>

          {/* Google sign-in (Google Identity Services) — hidden until a Client ID is configured */}
          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <>
              <div data-testid="google-login">
                <GoogleSignInButton role="candidate" text="signin_with" onCredential={handleGoogleCredential} />
              </div>
              <div className="flex items-center gap-3 my-6">
                <span className="h-px flex-1 bg-border" />
                <span className="font-mono text-[10px] text-muted-foreground tracking-widest">OR</span>
                <span className="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-muted-foreground/50 focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none transition-all"
                data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-[#0F172A]">Password</label>
                <Link to="/forgot-password" className="text-xs text-[#0025D8] font-medium hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-border bg-white px-4 py-3 pr-11 text-sm text-[#0F172A] placeholder:text-muted-foreground/50 focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none transition-all"
                  data-testid="login-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#0F172A] transition-colors"
                  aria-label={showPw ? "Hide password" : "Show password"}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={submitting} data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white py-3.5 font-semibold text-sm shadow-lg shadow-[#0025D8]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            New here? <Link to="/signup" className="text-[#0025D8] font-semibold hover:underline">Create account</Link>
          </p>
        </div>
      </div>

      {/* ══════════════ RIGHT — BRAND PANEL ══════════════ */}
      <AuthBrandPanel
        eyebrow="WHY VETRAHUB"
        title="Welcome back. Let's keep the momentum."
        subtitle="Your sessions, scores, and saved coaching are right where you left them."
      />
    </main>
  );
}
